import { defineAsyncComponent } from 'vue'
import { tabOutlineLayoutGrid } from 'quasar-extras-svg-icons/tabler-icons-v3'

import type { MainFeatureTwin, FeatureConfig, FeatureParameterConfigs } from 'src/models'

import type { DashboardTileTwin } from 'src/components/features/DashboardFeature'
import type { DashboardTileAction } from 'src/components/features/DashboardFeature'

/**
 * Configuration twin for the Grid dashboard feature.
 *
 * Mirrors `models/Feature/MainFeature/GridDashboardFeature.json`. It is a
 * separate interface from `DashboardFeatureTwin` on purpose: the two feature
 * types evolve independently and must not drift into each other. Only the tile
 * twin and the action shape are shared, and those are imported as types, so
 * nothing is shared at runtime.
 */
export interface GridDashboardFeatureTwin extends MainFeatureTwin {
  /** Configuration for parameters to be used in the dashboard. The parameters are used in the queries and can be used in the expressions. */
  parameters?: FeatureParameterConfigs
  /** The number of columns in the tile grid. Tile positions and widths in the layout are counted in these columns. Default is 12. */
  columns?: number
  /** The height of a single grid row in pixels. A tile with a height of 2 occupies twice this height. Default is 70px. */
  cellHeight?: number
  /** The inset around each tile cell in pixels. Default is 10px, matching GridStack. */
  /** GridStack-equivalent tile margin in pixels. Defaults to 4. */
  margin?: number
  /** If set, the grid uses fewer columns on narrow screens so that the tiles stay readable. */
  responsive?: boolean
  /** Disable floating will not allow for empty spaces in the grid. Tiles will always fill the grid. */
  disableFloat?: boolean
  /** hide toolbar */
  hideToolbar?: boolean
  /** hide toolbar menu */
  hideToolbarMenu?: boolean
  /** toolbar menu overflow */
  toolbarMenuOverflow?: boolean
  /** The color from the palette (eg. primary, secondary, positive, negative, information) or a hex value (eg. #e4610f) */
  toolbarBackgroundColor?: string
  /** Width in pixels at which the whole header content stops growing and is centred. The coloured bar behind it still spans the full width. Unset: full width. */
  headerContentMaxWidth?: number
  /**
   * @deprecated No longer read by any component. Use {@link headerContentMaxWidth} to cap and
   * centre the header content, and {@link toolbarBackgroundColor} for the controls strip colour.
   * Kept on the interface only so existing twins still type-check.
   */
  cssStyleToolbarHeader?: string
  /**
   * @deprecated No longer read by any component. Use {@link headerContentMaxWidth} to cap and
   * centre the header content, and {@link toolbarBackgroundColor} for the controls strip colour.
   * Kept on the interface only so existing twins still type-check.
   */
  cssStyleToolbar?: string
  /** Css style grid */
  cssStyleGrid?: string
  /** Show footer */
  enableFooter?: boolean
  /** Enable tour guide */
  enableTourGuide?: boolean
  /** Actions for the dashboard */
  actions?: { [key: string]: DashboardTileAction }
  /** Automatically sync selected twins to the store when selection changes */
  syncSelection?: boolean
  /** Always fetch selected twins from API even if already in the store */
  syncSelectionOverwrite?: boolean
}

/**
 * A dashboard tile as this feature consumes it: the shared `DashboardTile`
 * configuration twin plus the stored layout cell this grid positions it by.
 */
export interface GridDashboardTileTwin extends DashboardTileTwin {
  layout?: {
    x: number
    y: number
    width: number
    height: number
  }
}

const featureConfig: FeatureConfig = {
  main: {
    mainComponent: defineAsyncComponent(() => import('./GridDashboardFeatureMain.vue')),
    fullWidth: true,
    icon: tabOutlineLayoutGrid,
    label: 'Dashboard'
  }
}

export default featureConfig
